import React from "react";
import '../css/Projects.css';

// Import Images
import vantage from '../images/vantage.png';
import argus from '../images/argus.png';
import spicerack from '../images/spicerack.jpeg';
import truxpert from '../images/truxpert.jpeg';

// Import Icons
import { FaReact, FaJsSquare, FaCss3Alt, FaAws, FaJava } from 'react-icons/fa';
import { DiMysql } from 'react-icons/di';
import { SiSpringboot, SiDocker, SiGithub, SiTailwindcss, SiClaude, SiAmazonec2, SiAmazonrds } from 'react-icons/si';
import { MdOutlineLaunch } from "react-icons/md";
import { GeminiIcon } from "./GeminiIcon";

// Components
import Badge from "./Badge";
import Card from "./Card";
import MacWindow from "./MacWindow";

// --- JAVA PROJECTS DATA ---
const javaProjectsData = [
    {
        title: "Vantage",
        subtitle: "Gamified DSA Visualization Engine",
        description: "A visualization-first gamified approach to learning Data Structures and Algorithms.",
        accentColor: "#EDFF66",
        url: "vantagecode.tech",
        liveLink: "https://vantagecode.tech/",
        githubLink: "https://github.com/roguekishore/Vantage",
        image: vantage,
        stack: [
            { icon: <FaReact />, name: "React" },
            { icon: <SiSpringboot />, name: "SpringBoot" },
            { icon: <DiMysql />, name: "MySQL" },
            { icon: <FaAws />, name: "AWS" },
            { icon: <SiDocker />, name: "Docker" },
            { icon: <FaJava />, name: "Java" },
            { icon: <FaJsSquare />, name: "Javascript" },
            { icon: <SiTailwindcss />, name: "Tailwind" },
            { icon: <SiClaude />, name: "Claude" },
            { icon: <SiAmazonec2 />, name: "EC2" },
            { icon: <SiAmazonrds />, name: "RDS" },
        ]
    },
    {
        title: "Argus",
        subtitle: "Grievance Redressal System",
        description: "AI prioritizes, leadership escalates, and citizens verify closure. No delays - just transparent, accountable governance.",
        accentColor: "#ED66FF",
        url: "argusweb.tech",
        liveLink: "https://argusweb.tech/",
        githubLink: "https://github.com/roguekishore/Argus",
        image: argus,
        stack: [
            { icon: <FaReact />, name: "React" },
            { icon: <SiSpringboot />, name: "SpringBoot" },
            { icon: <DiMysql />, name: "MySQL" },
            { icon: <FaAws />, name: "AWS" },
            { icon: <SiDocker />, name: "Docker" },
            { icon: <FaJava />, name: "Java" },
            { icon: <FaJsSquare />, name: "Javascript" },
            { icon: <FaCss3Alt />, name: "CSS" },
            { icon: <GeminiIcon />, name: "Gemini" },
        ]
    },
    {
        title: "SpiceRack",
        subtitle: "Grocery-Recipe App",
        description: "A full-stack grocery application that lets users shop, manage inventory, create and discover recipes, plan meals, and get nutritional insights.",
        accentColor: "#ffffffff",
        url: "spicerack.netlify.app",
        liveLink: "https://spicerack.netlify.app/",
        githubLink: "https://github.com/roguekishore/SpiceRack",
        image: spicerack,
        stack: [
            { icon: <FaReact />, name: "React" },
            { icon: <SiSpringboot />, name: "SpringBoot" },
            { icon: <DiMysql />, name: "MySQL" },
            { icon: <FaAws />, name: "AWS" },
            { icon: <SiDocker />, name: "Docker" },
            { icon: <FaJava />, name: "Java" },
            { icon: <FaJsSquare />, name: "Javascript" },
            { icon: <FaCss3Alt />, name: "CSS" },
        ]
    },
    {
        title: "Truxpert",
        subtitle: "Food Truck Mgmt System",
        description: "A vendor application and management system for food truck registration, review, and inspector assignment in one unified platform.",
        accentColor: "#ff4400ff",
        url: "truxpert.netlify.app",
        liveLink: "https://truxpert.app/",
        githubLink: "https://github.com/roguekishore/Truxpert",
        image: truxpert,
        stack: [
            { icon: <FaReact />, name: "React" },
            { icon: <SiSpringboot />, name: "SpringBoot" },
            { icon: <DiMysql />, name: "MySQL" },
            { icon: <FaAws />, name: "AWS" },
            { icon: <SiDocker />, name: "Docker" },
            { icon: <FaJava />, name: "Java" },
            { icon: <FaJsSquare />, name: "Javascript" },
            { icon: <FaCss3Alt />, name: "CSS" },
        ]
    }
];

// Helper function to generate gradient CSS variables
const generateGradientVars = (hexColor) => {
    const hexToHSL = (hex) => {
        let r = parseInt(hex.slice(1, 3), 16) / 255;
        let g = parseInt(hex.slice(3, 5), 16) / 255;
        let b = parseInt(hex.slice(5, 7), 16) / 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        let h, s, l = (max + min) / 2;
        if (max === min) h = s = 0;
        else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
                case g: h = ((b - r) / d + 2) / 6; break;
                case b: h = ((r - g) / d + 4) / 6; break;
                default: h = 0;
            }
        }
        return { h: h * 360, s: s * 100, l: l * 100 };
    };

    const hsl = hexToHSL(hexColor);
    return {
        '--accent-500': `hsla(${hsl.h}, ${Math.min(hsl.s + 5, 100)}%, ${Math.min(hsl.l + 15, 75)}%, 0.35)`,
        '--accent-1000': `hsla(${hsl.h}, ${hsl.s}%, ${Math.max(hsl.l - 30, 5)}%, 0)`,
        '--accent-500-hover': `hsla(${hsl.h}, ${Math.min(hsl.s + 5, 100)}%, ${Math.min(hsl.l + 15, 75)}%, 0.5)`,
        '--accent-1000-hover': `hsla(${hsl.h}, ${hsl.s}%, ${Math.max(hsl.l - 30, 5)}%, 0)`,
    };
};

function JavaProjects() {
    return (
        <div className="projects-wrapper">
            
            <div className="purple-line-container-project-top">
                <div className="purple-line-project-top"></div>
            </div>
                    <h1 className="hero-title">Java Projects</h1>
                    <p className="hero-subtitle">
                        built by <a href="https://themaverick.tech" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>Kishore N E</a>
                    </p>
            <div className="projects-container">
                <Badge><span className="badge-star">✦</span>Java Projects</Badge>

                {javaProjectsData.map((project, index) => (
                    <Card key={index}>
                        <div className="projects-outer">
                            <div className="projects" style={generateGradientVars(project.accentColor)}>
                                <div className="purple-line-container-project">
                                    <div className="purple-line-project"></div>
                                </div>
                                <div className="project-heading">
                                    <div className="project-title">
                                        <h1 className="project-title-main">
                                            {project.title} <span className="bar">|</span>
                                            <span className="project-title-secondary"> {project.subtitle}</span>
                                        </h1>
                                    </div>
                                    <div className="project-description">
                                        <p>{project.description}</p>
                                    </div>
                                    <div className="project-stack">
                                        {project.stack.map((tech, i) => (
                                            <div key={i} className="stack-item">
                                                {tech.icon}
                                                <p>{tech.name}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="project-image">
                                    <MacWindow url={project.url}>
                                        <a href={project.liveLink} target="_blank" rel="noopener noreferrer">
                                            {project.video ? (
                                                <video src={project.video} autoPlay loop muted playsInline className="project-video">
                                                    Your browser does not support the video tag.
                                                </video>
                                            ) : (
                                                <img src={project.image} alt={`${project.title} Demo`} />
                                            )}
                                        </a>
                                    </MacWindow>
                                </div>
                                <div className="project-footer">
                                    <a href={project.liveLink} target="_blank" rel="noopener noreferrer">
                                        <button className="project-button"><MdOutlineLaunch />Live Website</button>
                                    </a>
                                    <a href={project.githubLink} target="_blank" rel="noopener noreferrer">
                                        <button className="project-button"><SiGithub />GitHub Repo</button>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </Card>
                ))}

                
            </div>
        </div>
    );
}

export default JavaProjects;
